import { Box, Typography } from "@mui/material";
import PropTypes from "prop-types";
import React from "react";

function StatCard({ label, value, unit, subtitle, color }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: { xs: 0, md: 2 },
      }}
    >
      <Typography sx={{ fontSize: 12, color: "#667085", fontWeight: 600 }}>
        {label}
      </Typography>
      {value ? (
        <Box
          sx={{ display: "flex", gap: "4px", alignItems: "baseline", color }}
        >
          <Typography sx={{ fontSize: 32, fontWeight: 700, lineHeight: 1 }}>
            {value}
          </Typography>
          <Typography sx={{ fontSize: 16, fontWeight: 600 }}>{unit}</Typography>
        </Box>
      ) : (
        <Typography sx={{ fontSize: 24, fontWeight: 600, color: "#667085" }}>
          No Data
        </Typography>
      )}
      <Typography sx={{ color: "#667085", fontSize: 12, whiteSpace: "nowrap" }}>
        {subtitle}
      </Typography>
    </Box>
  );
}

StatCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  unit: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  color: PropTypes.string,
};
StatCard.defaultProps = {
  value: null,
  color: "",
};

function getSpO2Status(spo2) {
  if (!spo2) return null;

  if (spo2 >= 95) {
    return {
      label: "Normal (95-100%)",
      color: "#00A656",
      bgColor: "#00A6560D",
      borderColor: "#00A65626",
    };
  }
  if (spo2 >= 90 && spo2 < 95) {
    return {
      label: "Mild Hypoxemia (90-94%)",
      color: "#FF9500",
      bgColor: "#FF95000D",
      borderColor: "#FF950026",
    };
  }
  if (spo2 < 90) {
    return {
      label: "Concerning (<90%)",
      color: "#D32F2F",
      bgColor: "#A600000D",
      borderColor: "#A6000326",
    };
  }
  return {
    label: "Unknown",
    color: "#667085",
    bgColor: "#6670850D",
    borderColor: "#66708526",
  };
}

export default function SpO2Card({ lastMeasures, isLoading }) {
  const spo2 = lastMeasures?.spo2_statistics;
  const lastSync = spo2?.last_sync
    ? new Date(spo2.last_sync).toLocaleString()
    : "-";

  const currentSpO2 = spo2?.current;
  const spo2Status = getSpO2Status(currentSpO2);

  const statistics = [
    {
      label: "Current",
      value: spo2?.current,
      unit: "%",
      subtitle: `Last sync: ${lastSync}`,
    },
    {
      label: "Minimum",
      value: spo2?.minimum,
      unit: "%",
      subtitle: "Lowest reading",
      color: "#FF1F56",
    },
    {
      label: "Maximum",
      value: spo2?.maximum,
      unit: "%",
      subtitle: "Highest reading",
      color: "#4280BE",
    },
  ];

  return (
    <Box
      sx={{
        border: "1px solid #E5E7EB",
        p: 2.5,
        borderRadius: 2,
        backgroundColor: "#FFF",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        opacity: isLoading ? 0.6 : 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography
            sx={{ fontWeight: 500, fontSize: 14, color: "#000000DE" }}
          >
            Blood Oxygen (SpO2)
          </Typography>
          {spo2Status && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                backgroundColor: spo2Status.bgColor,
                color: spo2Status.color,
                border: `1px solid ${spo2Status.borderColor}`,
                borderRadius: "8px",
                fontWeight: 500,
                fontSize: 12,
                p: "7px 8px",
              }}
            >
              <Box
                sx={{
                  width: "9px",
                  height: "9px",
                  borderRadius: "50%",
                  backgroundColor: spo2Status.color,
                }}
              />
              {spo2Status.label}
            </Box>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            sm: "repeat(2, 1fr)",
            lg: "repeat(3, 1fr)",
          },
          gap: 2.5,
          mb: 3,
        }}
      >
        <Box sx={{ gridColumn: { xs: "1 / -1", sm: "auto" } }}>
          <StatCard {...statistics[0]} />
        </Box>
        <StatCard {...statistics[1]} />
        <StatCard {...statistics[2]} />
      </Box>
    </Box>
  );
}

SpO2Card.propTypes = {
  lastMeasures: PropTypes.shape({
    spo2_statistics: PropTypes.shape({
      current: PropTypes.number,
      minimum: PropTypes.number,
      maximum: PropTypes.number,
      last_sync: PropTypes.string,
    }),
  }),
  isLoading: PropTypes.bool,
};

SpO2Card.defaultProps = {
  lastMeasures: {
    spo2_statistics: {
      current: null,
      minimum: null,
      maximum: null,
      last_sync: null,
    },
  },
  isLoading: false,
};
